import { Injectable, OnDestroy, inject } from '@angular/core';
import { User, DefaultUser } from 'src/app/models/user.model';
import { Observable, Subscription, BehaviorSubject, tap, catchError, EMPTY } from "rxjs";
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';

export enum SupportSubject {
  FEEDBACK = 'F',
  ORDER_ISSUE = 'O',
  EMAIL_ISSUE = 'E',
  BUG = 'B',
  FEATURE_REQUEST = 'R',
  PROFESSIONAL_CONTACT = 'P',
  OTHER = 'A',
}

export const SupportSubjectToLabelMapping: Record<SupportSubject, string> = {
  [SupportSubject.FEEDBACK]: "Feedback",
  [SupportSubject.ORDER_ISSUE]: "Order Issue",
  [SupportSubject.EMAIL_ISSUE]: "Email Issue",
  [SupportSubject.BUG]: "Bug",
  [SupportSubject.FEATURE_REQUEST]: "Feature Request",
  [SupportSubject.PROFESSIONAL_CONTACT]: "Professional Contact",
  [SupportSubject.OTHER]: 'Other'
};

@Injectable({
  providedIn: 'root'
})
export class AccountService implements OnDestroy {
  private userSubject: BehaviorSubject<User>;
  public userObservable: Observable<User>;
  user: User;
  private userSub: Subscription;
  private checkAccountSub: Subscription | null = null;

  timerRef: ReturnType<typeof setInterval> | undefined;
  canCheckAccountStatus = false;

  private http = inject(HttpClient);
  private cookieService = inject(CookieService);
  private router = inject(Router);

  constructor() {
    try {
      const stored = localStorage.getItem('user');
      this.user = stored ? JSON.parse(stored) : DefaultUser;
    } catch {
      this.user = DefaultUser;
    }
    this.userSubject = new BehaviorSubject<User>(this.user);
    this.userObservable = this.userSubject.asObservable();
    this.canCheckAccountStatus = true;
    this.userSub = this.userObservable.subscribe(
      (next: User) => {
        this.user = next;
      },
      (_error) => {}
    )
  }

  ngOnDestroy() {
    this.userSub.unsubscribe();
    if (this.checkAccountSub) {
      this.checkAccountSub.unsubscribe();
    }
    clearInterval(this.timerRef);
  }

  public setUser(newUser: User) {
    if (newUser.isLogin) {
      this.startTimer();
    }
    this.userSubject.next(newUser);
    localStorage.setItem('user', JSON.stringify(newUser));
  }

  setIsLogin(isLogin: boolean) {
    this.user.isLogin = isLogin;
    this.setUser(this.user);
  }

  login(username: string, password: string): Observable<any> {
    const headers = new HttpHeaders()
    const body = {
      "username": username,
      "password": password
    };
    return this.http.post<any>(environment.apiURL + '/services/auth', body, { headers, observe: 'response', withCredentials: true })
      .pipe(tap(response => {
        if (response.status == 200) {
          const jsonObj = response.body as User;
          jsonObj.isLogin = true;
          this.setUser(jsonObj);
        }
        else {
          const jsonObj = response.body as User;
          jsonObj.isLogin = false;
          this.setUser(jsonObj);
        }
      }));
  }

  logout(): Observable<any> {
    const headers = new HttpHeaders()
    const body = {}
    return this.http.post<any>(environment.apiURL + '/services/logout', body, { headers, observe: 'response', withCredentials: true })
      .pipe(tap(response => {
        if (response.status == 200) {
          this.canCheckAccountStatus = true;
          localStorage.removeItem('user');
          this.cookieService.deleteAll();
          this.user.isLogin = false;
          this.userSubject.next(this.user);
        }
      }));
  }

  acceptTermsAndConditions(): Observable<any> {
    const headers = new HttpHeaders()
    const body = {}
    return this.http.post<any>(environment.apiURL + '/services/accept-terms-and-conditions', body, { headers, observe: 'response', withCredentials: true })
      .pipe(tap(response => {
        if (response.status == 200) {
          this.user.has_accepted_termsAndConditions = true;
          this.setUser(this.user);
        }
      }));
  }

  /**
   * Boot-time session check. Verifies the server session once on app startup
   * (APP_INITIALIZER), regardless of the cached login state, so that a
   * server-side login performed via a full-page redirect (e.g. Steam,
   * password-reset, email-change) is detected after the SPA reloads.
   *
   * On failure it stays logged-out WITHOUT redirecting, so public pages
   * remain browsable by anonymous visitors.
   */
  initSessionCheck(): Observable<HttpResponse<User>> {
    const headers = new HttpHeaders();
    return this.http.get<User>(environment.apiURL + '/services/get-user-infos', { headers, observe: 'response', withCredentials: true })
      .pipe(
        tap((response) => {
          const jsonObj = response.body as User;
          jsonObj.isLogin = true;
          this.setUser(jsonObj);
        }),
        catchError((_error) => {
          this.user.isLogin = false;
          localStorage.removeItem('user');
          this.userSubject.next(this.user);
          return EMPTY;
        })
      );
  }

  checkAccountStatus() {
    if (this.canCheckAccountStatus) {
      this.startTimer();
    }
    else return;

    const headers = new HttpHeaders()
    if (this.checkAccountSub) {
      this.checkAccountSub.unsubscribe();
    }
    this.checkAccountSub = this.http.get(environment.apiURL + '/services/get-user-infos', { headers, observe: 'response', withCredentials: true })
      .subscribe(
        (response) => {
          const jsonObj = response.body as User;
          this.user = jsonObj;
          this.user.isLogin = true;
          this.setUser(this.user);
        },
        (_error) => {
          localStorage.removeItem('user');
          this.cookieService.deleteAll();
          this.user.isLogin = false;
          this.userSubject.next(this.user);
          this.router.navigate(['/'])
        })
  }

  updateAccountOwner(firstname: string, lastName: string): Observable<any> {
    const headers = new HttpHeaders()
    const body = {
      "first_name": firstname,
      "last_name": lastName
    };
    return this.http.post(environment.apiURL + '/services/update-account-owner-infos', body, { headers, observe: 'response', withCredentials: true })
      .pipe(tap(response => {
        if (response.status == 200) {
          this.user.first_name = firstname;
          this.user.last_name = lastName;
          this.setUser(this.user);
        }
      }));
  }

  clearUnverifiedEmail(): Observable<any> {
    const headers = new HttpHeaders()
    const body = {};
    return this.http.post(environment.apiURL + '/services/email/ClearUnverifiedEmail', body, { headers, observe: 'response', withCredentials: true })
      .pipe(tap(response => {
        if (response.status == 200) {
          this.user.has_verified_email = false;
          this.user.email = "";
          this.setUser(this.user);
        }
      }));
  }

  updatePassword(lastPassword: string, password: string): Observable<any> {
    const headers = new HttpHeaders()
    const body = {
      "last_password": lastPassword,
      "new_password": password
    };
    return this.http.post(environment.apiURL + '/services/update-password', body, { headers, observe: 'response', withCredentials: true })
      .pipe(tap(response => {
        if (response.status == 200) {
          const jsonObj = response.body as User;
          jsonObj.isLogin = true;
          this.setUser(jsonObj);
        }
      }));
  }

  setPassword(password: string): Observable<any> {
    const headers = new HttpHeaders()
    const body = {
      "new_password": password
    };
    return this.http.post(environment.apiURL + '/services/set-password', body, { headers, observe: 'response', withCredentials: true })
      .pipe(tap(response => {
        if (response.status == 200) {
          const jsonObj = response.body as User;
          jsonObj.isLogin = true;
          this.setUser(jsonObj);
        }
      }));
  }

  sendSupportMessage(name: string, email: string, subject: SupportSubject, message: string, copy: boolean): Observable<any> {
    const headers = new HttpHeaders()
    const body = {
      "Name": name,
      "Email": email,
      "message": message,
      "copy": copy,
      "subject": subject
    };
    return this.http.post<any>(environment.apiURL + '/services/send-support-message', body, { headers, observe: 'response', withCredentials: true })
      .pipe(tap(() => {}));
  }

  subscribeAccountToNewsLetter(value: boolean): Observable<any> {
    const headers = new HttpHeaders()
    const body = {"subscribeToNewsletter": value};
    return this.http.post(environment.apiURL + '/services/suscribe-account-to-newsletter', body, { headers, observe: 'response', withCredentials: true })
      .pipe(tap(response => {
        if (response.status == 200) {
          this.user.has_subscribed_to_newsletter = value;
          this.setUser(this.user);
        }
      }));
  }

  setEmail(value: string): Observable<any> {
    const headers = new HttpHeaders()
    const body = { "email": value };
    return this.http.post(environment.apiURL + '/services/email/set-email', body, { headers, observe: 'response', withCredentials: true })
      .pipe(tap(response => {
        if (response.status == 200) {
          this.user.email = value;
          this.setUser(this.user);
        }
      }));
  }

  sendMailActivation(): Observable<any> {
    const headers = new HttpHeaders()
    const body = { };
    return this.http.post(environment.apiURL + '/services/email/send-mail-activation', body, { headers, observe: 'response', withCredentials: true })
      .pipe(tap(() => {}));
  }

  changeAvatarPic(value: number): Observable<any> {
    const headers = new HttpHeaders()
    const body = { "icon_index": value };
    return this.http.post(environment.apiURL + '/services/update-avatar', body, { headers, observe: 'response', withCredentials: true })
      .pipe(tap(response => {
        if (response.status == 200) {
          this.user.icon_index = value;
          this.setUser(this.user);
        }
      }));
  }

  subscribeMailToNewsLetter(email: string): Observable<any> {
    const headers = new HttpHeaders()
    const body = { "email": email };
    return this.http.post(environment.apiURL + '/services/suscribe-email-to-newsletter', body, { headers, observe: 'response', withCredentials: true })
  }

  checkCreatorCode(creator_code: string): Observable<any> {
    const headers = new HttpHeaders()
    const body = { "creator_code": creator_code };
    return this.http.post(environment.apiURL + '/services/check-creator-code', body, { headers, observe: 'response', withCredentials: true })
  }

  getErrorStr(status: number): string {
    if (status === undefined) return "Unable to connect";
    let str = "";
    switch (status) {
      case 400:
        str += "BAD_REQUEST contact admin";
        break;
      case 403:
        str += "Invalid username or password";
        break;
      case 409:
        str += "CONFLICT contact admin";
        break;
      case 429:
        str += "Too Many Requests";
        break;
      case 504:
        str += "Timeout";
        break;
      default:
        break;
    }
    return str;
  }

  startTimer() {
    if (this.timerRef) {
      clearInterval(this.timerRef);
    }
    this.canCheckAccountStatus = false;
    this.timerRef = setInterval(() => {
      this.canCheckAccountStatus = true;
      clearInterval(this.timerRef);
    }, 20000);
  }
}
